import { normalizeRTSP, rtspTargetURL } from "../rtspTarget.mjs";
import { defaultSEI } from "../sei.mjs";
import { normalizeWhip, safeWhipEndpoint } from "../whip.mjs";
import { normalizeLlHls } from "../llHls.mjs";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Toolbar,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  DataGrid,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  GridCsvExportMenuItem,
  GridPrintExportMenuItem,
  GridToolbarExportContainer,
  type GridColDef,
  type GridRowId,
  type GridRowSelectionModel
} from "@mui/x-data-grid";
import StreamTargetDlg from "./StreamTargetDlg";
import type { PcapReplaySource, StreamTargetForm } from "../types/streamTargets";
import { useAppContext } from "../context/AppContext";

type StreamOutputsProps = {
  targets: StreamTargetForm[];
  onChange: (targets: StreamTargetForm[]) => void;
  networkInterfaceOptions: string[];
  isNetworkInterfacesLoading: boolean;
  loadNetworkInterfaces: (currentValue?: string) => Promise<void>;
  disabled?: boolean;
  readOnly?: boolean;
  platformName?: string;
  streamName?: string;
  capturedDestination?: { ip: string; port: number } | null;
  rawPcapEnabled?: boolean;
  pcapSource?: PcapReplaySource | null;
  authorizedFetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  flywheelEnabled?: boolean;
  flywheelProcessorId?: string;
  showInputSelector?: boolean;
  udsDatagramAvailable?: boolean;
};

type StreamOutputRow = {
  id: string;
  name: string;
  description: string;
  data: string;
  payload: string;
};

const createTargetId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `target-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
};

const normalizeSelectionIds = (ids?: Set<unknown> | Iterable<unknown>): Set<GridRowId> => {
  if (!ids) {
    return new Set<GridRowId>();
  }
  const sourceIds = ids instanceof Set ? ids : new Set(ids);
  const normalizedIds = new Set<GridRowId>();
  for (const id of sourceIds) {
    if (typeof id === "string" || typeof id === "number") {
      normalizedIds.add(id);
    }
  }
  return normalizedIds;
};

const StreamOutputs = ({
  targets,
  onChange,
  networkInterfaceOptions,
  isNetworkInterfacesLoading,
  rawPcapEnabled,
  pcapSource,
  authorizedFetch,
  loadNetworkInterfaces,
  disabled,
  readOnly = false,
  platformName,
  streamName,
  capturedDestination,
  flywheelEnabled = false,
  flywheelProcessorId = "",
  showInputSelector = true,
  udsDatagramAvailable = true,
}: StreamOutputsProps) => {
  const { serverConfig } = useAppContext();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set<GridRowId>()
  });
  const [searchText, setSearchText] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTargetId, setEditingTargetId] = useState<string | null>(null);

  const rows = useMemo<StreamOutputRow[]>(
    () =>
      targets.map((target) => ({
        payload: target.payloadMode === "h264Sei" ? "SEI metadata" : "Full stream",
        id: target.id,
        name: target.name,
        description: target.description,
        data:
          target.type === "udp" && target.destinationMode === "captured"
            ? `Captured destination (${capturedDestination?.ip || "unavailable"}:${capturedDestination?.port || ""})`
            : target.type === "rtsp"
              ? rtspTargetURL(target.rtsp.path, serverConfig.rtspPlaybackBaseUrl, window.location.hostname) || target.rtsp.path || "Automatic RTSP playback URL (available after saving)"
              : target.type === "whip" ? safeWhipEndpoint(target.url) : target.type === "llhls" ? String(target.llhls.hlsPlaybackUrl || "Automatic playback URL") : target.type === "rawPcap"
              ? `${target.rawPcap.mode} via ${target.rawPcap.interfaceId || "unselected interface"}`
              : target.url
      })),
    [capturedDestination, targets, serverConfig.rtspPlaybackBaseUrl]
  );

  const normalizedSearch = searchText.trim().toLowerCase();
  const filteredRows = useMemo(() => {
    if (!normalizedSearch) {
      return rows;
    }
    return rows.filter((row) => {
      const name = row.name.toLowerCase();
      const data = row.data.toLowerCase();
      return name.includes(normalizedSearch) || data.includes(normalizedSearch);
    });
  }, [normalizedSearch, rows]);

  const columns = useMemo<GridColDef<StreamOutputRow>[]>(
    () => [
      {
        field: "name",
        headerName: "Output Name",
        flex: 1,
        minWidth: 180,
        renderCell: (params) => {
          const description = params.row.description?.trim() || "No description";
          return (
            <Tooltip title={description} placement="top" describeChild>
              <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                  {params.row.name}
                </Typography>
              </Box>
            </Tooltip>
          );
        }
      },
      {
        field: "payload", headerName: "Payload", minWidth: 135, flex: 0.6
      },
      {
        field: "data",
        headerName: "Url",
        flex: 2,
        minWidth: 240
      }
    ],
    []
  );

  const hasSelection = selectionModel.ids.size > 0;
  const hasSingleSelection = selectionModel.ids.size === 1;

  const selectedTarget = useMemo(() => {
    if (!hasSingleSelection) {
      return null;
    }
    const [id] = Array.from(selectionModel.ids);
    return targets.find((target) => target.id === id) ?? null;
  }, [hasSingleSelection, selectionModel.ids, targets]);

  const handleAdd = () => {
    if (disabled || readOnly) {
      return;
    }
    setEditingTargetId(null);
    setIsDialogOpen(true);
  };

  const hasCapturedDestinationTarget = targets.some(
    (target) => target.type === "udp" && target.destinationMode === "captured"
  );

  const handleAddCapturedDestination = () => {
    if (disabled || readOnly || !capturedDestination || hasCapturedDestinationTarget) {
      return;
    }
    onChange([
      ...targets,
      {
        payloadMode: "fullStream",
        sei: defaultSEI(),
        id: createTargetId(),
        inputType: "source",
        inputId: "",
        name: "Captured destination",
        description: "Replay UDP payloads to the destination stored in the selected capture flow",
        type: "udp",
        destinationMode: "captured",
        url: "",
        networkInterface: "",
        ttl: 64,
        bufferSize: 4096,
        udsMode: "connection",
        srt: {
          latency: 120,
          passphrase: "",
          pbkeylen: 16,
          streamid: "",
          transtype: "live",
          blocking: false
        },
        rtsp: normalizeRTSP(),
  llhls: normalizeLlHls(),
        whip: normalizeWhip(),
        rawPcap: {
          mode: "rewrite",
          interfaceId: "",
          destinationIp: "",
          destinationPort: "",
          destinationMac: "",
          allowSpoofedSource: false,
          queueSize: 256
        }
      }
    ]);
  };

  const handleDelete = () => {
    if (!hasSelection || disabled || readOnly) {
      return;
    }
    onChange(targets.filter((target) => !selectionModel.ids.has(target.id)));
    setSelectionModel({ type: "include", ids: new Set() });
  };

  const handleEdit = () => {
    if (!hasSingleSelection || (disabled && !readOnly)) {
      return;
    }
    setEditingTargetId(selectedTarget?.id ?? null);
    setIsDialogOpen(true);
  };

  const handleInjectErrors = () => {
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingTargetId(null);
  };

  const handleDialogSave = (target: StreamTargetForm) => {
    if (disabled || readOnly) return;
    const existingId = editingTargetId ?? target.id;
    if (editingTargetId) {
      onChange(
        targets.map((item) => (item.id === existingId ? { ...target, id: existingId } : item))
      );
    } else {
      const nextId = target.id.trim() ? target.id : createTargetId();
      onChange([...targets, { ...target, id: nextId }]);
    }
    setSelectionModel({ type: "include", ids: new Set() });
    handleDialogClose();
  };

  const dialogTarget = editingTargetId ? selectedTarget : null;
  const reservedNames = useMemo(
    () =>
      targets
        .filter((target) => target.id !== editingTargetId)
        .map((target) => target.name.trim()),
    [editingTargetId, targets]
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={readOnly ? <SettingsIcon /> : <EditIcon />}
            onClick={handleEdit}
            disabled={!hasSingleSelection || (disabled && !readOnly)}
          >
            {readOnly ? "Show Config" : "Edit"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={!hasSelection || disabled || readOnly}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={disabled || readOnly}
          >
            Add
          </Button>
          {capturedDestination ? (
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddCapturedDestination}
              disabled={disabled || readOnly || hasCapturedDestinationTarget}
            >
              Add captured destination
            </Button>
          ) : null}
        </Stack>
        {/* <Button
          variant="text"
          size="small"
          startIcon={<BugReportOutlinedIcon />}
          onClick={handleInjectErrors}
          disabled={disabled}
        >
          Inject errors
        </Button> */}
      </Stack>
      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick={false}
          autoHeight
          density="compact"
          rowHeight={40}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 5 } }
          }}
          onRowSelectionModelChange={(nextSelection) => {
            if (Array.isArray(nextSelection)) {
              setSelectionModel({ type: "include", ids: new Set<GridRowId>(nextSelection) });
              return;
            }
            if (nextSelection && typeof nextSelection === "object" && "ids" in nextSelection) {
              const ids = (nextSelection as { ids?: Set<unknown> | Iterable<unknown> }).ids;
              setSelectionModel({ type: "include", ids: normalizeSelectionIds(ids) });
              return;
            }
            setSelectionModel({ type: "include", ids: new Set<GridRowId>() });
          }}
          rowSelectionModel={selectionModel}
          slots={{
            toolbar: () => (
              <Toolbar variant="dense" sx={{ px: 1, pt: 1 }}>
                <ColumnsPanelTrigger />
                <FilterPanelTrigger />
                <GridToolbarExportContainer>
                  <GridCsvExportMenuItem />
                  <GridPrintExportMenuItem />
                </GridToolbarExportContainer>
                <Box sx={{ flexGrow: 1 }} />
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Toolbar>
            )
          }}
          sx={{
            border: "none",
          }}
        />
      </Box>
      <StreamTargetDlg
        readOnly={readOnly}
        open={isDialogOpen}
        initialTarget={dialogTarget}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        reservedNames={reservedNames}
        networkInterfaceOptions={networkInterfaceOptions}
        isNetworkInterfacesLoading={isNetworkInterfacesLoading}
        loadNetworkInterfaces={loadNetworkInterfaces}
        platformName={platformName}
        streamName={streamName}
        capturedDestination={capturedDestination}
        rawPcapEnabled={rawPcapEnabled}
        pcapSource={pcapSource}
        authorizedFetch={authorizedFetch}
        flywheelEnabled={flywheelEnabled}
        flywheelProcessorId={flywheelProcessorId}
        showInputSelector={showInputSelector}
        udsDatagramAvailable={udsDatagramAvailable}
      />
    </Stack>
  );
};

export default StreamOutputs;
